import { useHistory } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  CircularProgress,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { ArrowForwardOutlined, HistoryOutlined } from "@mui/icons-material";
import ArticleIcon from "@mui/icons-material/Article";

import { timeAgo } from "src/reusable/Functions";
import { cleanArtifactFilename } from "src/utils/artifactUtils";
import {
  useGetVnvAssistRuns,
  type VnvAssistRunStatus,
  type VnvAssistRunSummary,
} from "../services/VnvAssistServices";

type ChipPropsLite = {
  label: string;
  color: "default" | "info" | "success" | "error";
  icon?: React.ReactElement;
};

export const chipForStatus = (
  status: VnvAssistRunStatus,
  claimCount: number,
): ChipPropsLite => {
  switch (status) {
    case "pending":
      return { label: "Queued", color: "default" };
    case "identifying":
      return {
        label: "Identifying",
        color: "info",
        icon: <CircularProgress size={10} />,
      };
    case "identified":
      return {
        label: `${claimCount} claim${claimCount !== 1 ? "s" : ""}`,
        color: "success",
      };
    case "failed":
      return { label: "Failed", color: "error" };
  }
};

const VnvAssistRunRow = ({ run }: { run: VnvAssistRunSummary }) => {
  const history = useHistory();
  const filename = run.input_artifact_filename
    ? cleanArtifactFilename(run.input_artifact_filename)
    : "Untitled run";
  const chip = chipForStatus(run.status, run.claim_count);

  return (
    <Card variant="outlined">
      <CardActionArea
        onClick={() =>
          history.push(`/vnv-assist/chat/${run.id}/${run.dialogue_id}`)
        }
        sx={{ p: 1.5 }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{ minWidth: 0 }}
        >
          <ArticleIcon color="primary" sx={{ flexShrink: 0 }} />
          <Typography
            variant="subtitle1"
            noWrap
            sx={{ flex: 1, minWidth: 0, fontWeight: 500 }}
          >
            {filename}
          </Typography>
          <Chip
            size="small"
            label={chip.label}
            color={chip.color}
            icon={chip.icon}
            variant="outlined"
            sx={{ flexShrink: 0 }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ flexShrink: 0, minWidth: 80, textAlign: "right" }}
          >
            {timeAgo(run.create_date)}
          </Typography>
        </Stack>
      </CardActionArea>
    </Card>
  );
};

type VnvAssistRecentRunsProps = {
  /** Maximum runs to show. Defaults to 3 (splash widget). */
  limit?: number;
  /** When true, render the "View more" link to /vnv-assist/recents. */
  showViewMore?: boolean;
};

const VnvAssistRecentRuns = ({
  limit = 3,
  showViewMore = true,
}: VnvAssistRecentRunsProps) => {
  const history = useHistory();
  const { data: page, isLoading, isError } = useGetVnvAssistRuns(limit);
  const visibleRuns = page?.runs ?? [];

  return (
    <Box sx={{ py: 4, maxWidth: "830px", width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <HistoryOutlined color="inherit" />
          <Typography variant="body1">Recent Runs</Typography>
        </Box>
        {showViewMore && (
          <Button
            endIcon={<ArrowForwardOutlined />}
            variant="text"
            color="inherit"
            onClick={() => history.push("/vnv-assist/recents")}
            data-attr="vnv-assist:link:view-all-recent-runs"
          >
            View more
          </Button>
        )}
      </Box>

      {isLoading && (
        <Stack spacing={1}>
          {Array.from({ length: limit }).map((_, i) => (
            <Skeleton key={i} variant="rectangular" height={56} />
          ))}
        </Stack>
      )}

      {isError && (
        <Typography color="error" align="center" sx={{ py: 2 }}>
          We're having trouble fetching recent runs.
        </Typography>
      )}

      {!isLoading && !isError && visibleRuns.length === 0 && (
        <Typography color="text.secondary" align="center" sx={{ py: 2 }}>
          No VNV Assist runs yet.
        </Typography>
      )}

      {visibleRuns.length > 0 && (
        <Stack spacing={1}>
          {visibleRuns.map((run) => (
            <VnvAssistRunRow key={run.id} run={run} />
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default VnvAssistRecentRuns;
